import type { User } from '@/@types/user'
import { UNREAD_NOTIFICATION_COUNT_QUERY_OPTIONS } from '@/utils/fetchers/notification'
import { useTabClickNotifyer } from '@/utils/hooks/useTabClickListener'
import { useCurrentUser } from '@/utils/hooks/useUser'
import { css } from '@emotion/react'
import { MoreIcon } from '@ruguoapp/jike-fe-icons'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { isEqual, omit } from 'lodash-es'
import { type HTMLAttributes, memo } from 'react'
import UnreadNotificationTooltip from '../UnreadNotificationTooltip'
import SidebarTab from './SidebarTab'
import { Tab } from './config'
import { ExploreIcon, HomeIcon, NotificationActiveIcon, NotificationIcon } from './icons'
import { useActiveTab } from './util'

export interface SidebarTabsProps extends HTMLAttributes<HTMLDivElement> {}

// 优化 tabs 配置，将其移到组件外部
const createTabs = (unreadCount: number, user: User) => [
	{
		icon: <HomeIcon />,
		title: '动态',
		id: Tab.FOLLOWING_POST,
		link: '/following',
	},
	{
		icon: <ExploreIcon />,
		title: '发现',
		id: Tab.EXPLORE_POST,
		link: '/explore',
	},
	{
		icon: (
			<UnreadNotificationTooltip data={unreadCount}>
				{unreadCount > 0 ? <NotificationActiveIcon /> : <NotificationIcon />}
			</UnreadNotificationTooltip>
		),
		title: '通知',
		id: Tab.NOTIFICATION,
		link: '/notification',
	},
	{
		icon: (
			<svg
				width="28"
				height="28"
				viewBox="0 0 28 28"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path
					d="M23.3332 24.5002C23.3332 23.2625 22.8415 22.0755 21.9663 21.2003C21.0912 20.3252 19.9042 19.8335 18.6665 19.8335H9.33317C8.09549 19.8335 6.90851 20.3252 6.03334 21.2003C5.15817 22.0755 4.6665 23.2625 4.6665 24.5002"
					stroke="currentColor"
					strokeWidth="2.1"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
				<path
					d="M13.9998 15.1667C17.2215 15.1667 19.8332 12.555 19.8332 9.33333C19.8332 6.11167 17.2215 3.5 13.9998 3.5C10.7782 3.5 8.1665 6.11167 8.1665 9.33333C8.1665 12.555 10.7782 15.1667 13.9998 15.1667Z"
					stroke="currentColor"
					strokeWidth="2.1"
					strokeLinecap="round"
					strokeLinejoin="round"
				/>
			</svg>
		),
		title: '个人主页',
		id: Tab.USER_PROFILE,
		link: `/u/${user.username}`,
	},
	{
		icon: <MoreIcon />,
		title: '更多',
		id: Tab.MORE,
		link: '/more',
	},
]

export default memo(function SidebarTabs({ ...props }: SidebarTabsProps) {
	const activeTab = useActiveTab()
	const user = useCurrentUser()

	const { data: unreadCount = 0 } = useQuery(UNREAD_NOTIFICATION_COUNT_QUERY_OPTIONS)

	const tabs = createTabs(unreadCount, user)

	const notiTabClick = useTabClickNotifyer()

	return (
		<div
			css={css`
				display: flex;
				flex-direction: column;
				gap: 12px;
			`}
			{...props}
		>
			{tabs.map((item) => {
				const isActive = activeTab === item.id
				return (
					<Link
						to={item.link}
						key={item.id}
						aria-label={item.title}
						onClick={(e) => {
							if (isActive) {
								notiTabClick(item.id)
								e.preventDefault()
							}
						}}
						preload="viewport"
					>
						<SidebarTab {...omit(item, 'link')} isActive={isActive} />
					</Link>
				)
			})}
		</div>
	)
}, isEqual)
